import React, { useEffect, useRef, useState } from 'react';
import ScreenshotUploader from './ScreenshotUploader';

const SOURCE_META = {
  email:  { label: 'From email', icon: '✉', color: 'var(--blue-700)', bg: 'var(--blue-50)' },
  upload: { label: '',           icon: '',  color: 'var(--text-secondary)', bg: 'transparent' },
};
function roleLabel(r) {
  if (['admin', 'super_admin', 'manager', 'agent'].includes(r)) return { label: 'Agent', icon: '🎧', color: '#0B6623' };
  if (['customer', 'customer_manager'].includes(r)) return { label: 'Customer', icon: '👤', color: 'var(--text-secondary)' };
  return { label: '', icon: '', color: 'var(--text-secondary)' };
}

export default function TicketAttachments({ ticketId, attachments = [], canEdit, isStaff, currentUserId, onChange }) {
  const [adding, setAdding] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [zoom, setZoom] = useState(null);
  const [urls, setUrls] = useState({});          // att.id -> object URL (fetched once, reused)
  const cache = useRef({});                        // att.id -> object URL (for cleanup)

  // Fetch each attachment's image once; reuse the blob for thumbnail + lightbox.
  useEffect(() => {
    const token = sessionStorage.getItem('kgreen_token');
    let live = true;
    attachments.forEach(att => {
      if (cache.current[att.id]) return; // already fetched
      cache.current[att.id] = 'loading';
      fetch(`/api/tickets/${ticketId}/attachments/${att.id}/file`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.blob() : Promise.reject())
        .then(b => { if (!live) return; const u = URL.createObjectURL(b); cache.current[att.id] = u; setUrls(p => ({ ...p, [att.id]: u })); })
        .catch(() => { delete cache.current[att.id]; });
    });
    return () => { live = false; };
  }, [attachments, ticketId]);

  // Revoke all object URLs on unmount.
  useEffect(() => () => { Object.values(cache.current).forEach(u => { if (u && u !== 'loading') URL.revokeObjectURL(u); }); }, []);

  async function upload() {
    if (!items.length) return;
    setBusy(true); setErr('');
    try {
      const token = sessionStorage.getItem('kgreen_token');
      const fd = new FormData();
      items.forEach(it => fd.append('files', it.file, it.file.name));
      const res = await fetch(`/api/tickets/${ticketId}/attachments`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      if (data.errors?.length) setErr(data.errors.map(e => `${e.file}: ${e.error}`).join('; '));
      items.forEach(it => URL.revokeObjectURL(it.previewUrl));
      setItems([]); setAdding(false);
      onChange && onChange();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  async function remove(att) {
    if (!window.confirm('Remove this screenshot?')) return;
    setBusy(true); setErr('');
    try {
      const token = sessionStorage.getItem('kgreen_token');
      const res = await fetch(`/api/tickets/${ticketId}/attachments/${att.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      onChange && onChange();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const atMax = attachments.length >= 3;
  const Thumb = ({ att, style, onClick }) => {
    const u = urls[att.id];
    if (!u) return <div style={{ ...style, background: 'var(--n-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /></div>;
    return <img src={u} alt={att.filename} onClick={onClick} style={style} />;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>Screenshots{attachments.length ? ` · ${attachments.length}` : ''}</span>
        {canEdit && !atMax && !adding && (
          <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>📎 Add screenshot</button>
        )}
      </div>

      {err && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>⚠ {err}</div>}

      {adding && (
        <div style={{ marginBottom: 12, border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 12, background: 'var(--bg-app)' }}>
          <ScreenshotUploader items={items} setItems={setItems} disabled={busy} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { items.forEach(it => URL.revokeObjectURL(it.previewUrl)); setItems([]); setAdding(false); setErr(''); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={busy || !items.length} onClick={upload} style={{ background: '#0B6623' }}>{busy ? 'Uploading…' : `Upload ${items.length || ''}`}</button>
          </div>
        </div>
      )}

      {attachments.length === 0 ? (
        !adding && <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontStyle: 'italic', margin: 0 }}>No screenshots.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
          {attachments.map(att => {
            const rl = roleLabel(att.uploader_role);
            const isEmail = att.source === 'email';
            const tag = isEmail ? SOURCE_META.email : rl;
            const mine = att.uploaded_by && att.uploaded_by === currentUserId;
            return (
              <div key={att.id} style={{ border: `1px solid ${isEmail ? 'var(--blue-200, #bfdbfe)' : 'var(--border-subtle)'}`, borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                <Thumb att={att} onClick={() => setZoom(att)}
                  style={{ width: '100%', height: 72, objectFit: 'cover', cursor: 'pointer', display: 'block' }} />
                <div style={{ padding: '4px 7px' }}>
                  <span style={{ fontSize: 10, color: tag.color, fontWeight: 600 }}>{tag.icon} {tag.label}</span>
                </div>
                {(isStaff || mine) && (
                  <button onClick={() => remove(att)} disabled={busy} aria-label="Remove"
                    style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: 1 }}>✕</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox — reuses the already-loaded blob, so it opens instantly */}
      {zoom && (
        <div onClick={() => setZoom(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          {urls[zoom.id]
            ? <img src={urls[zoom.id]} alt={zoom.filename} style={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 6 }} />
            : <span className="spinner" style={{ width: 32, height: 32 }} />}
          <button onClick={() => setZoom(null)} aria-label="Close" style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', fontSize: 22, width: 40, height: 40, borderRadius: 8, cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </div>
  );
}
